/**
 * ExitConfirmDialog — 홈에서 뒤로가기 시 노출되는 앱 종료 확인 다이얼로그.
 *
 * RootLayout 에서 단 한 번 마운트되는 전역 컴포넌트. `useExitConfirm` store 의
 * isOpen 을 읽어 표시 여부를 결정.
 *
 * 동작:
 *   - "종료" → useExitConfirm.close() + native exitApp(). web/dev 에서는 exitApp 은 no-op.
 *   - "취소" / backdrop / 뒤로가기 한 번 더 → close (다이얼로그만 닫힘).
 *
 * 다이얼로그가 열려있는 동안 useDialogBackBinding 으로 activityStore.dialog 를
 * 점유 → 하드웨어 백을 한 번 더 누르면 resolveBack 이 close-dialog 를 1순위로
 * 반환해 다이얼로그가 닫힌다 (앱이 종료되지 않음).
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./ConfirmDialog";
import { useExitConfirm } from "../../store/exitConfirmStore";
import { useDialogBackBinding } from "../../navigation/useDialogBackBinding";
import { exitApp } from "../../native/app";

export default function ExitConfirmDialog() {
  const { t } = useTranslation(["app", "common"]);
  const isOpen = useExitConfirm((s) => s.isOpen);
  const close = useExitConfirm((s) => s.close);

  useDialogBackBinding({ isOpen, close, kind: "exit-confirm" });

  const handleExit = useCallback(() => {
    close();
    void exitApp();
  }, [close]);

  return (
    <ConfirmDialog
      open={isOpen}
      onClose={close}
      variant="destructive"
      title={t("exit.title")}
      description={t("exit.description")}
      secondaryText={t("common:action.cancel")}
      primaryText={t("common:action.exit")}
      onSecondaryPressed={close}
      onPrimaryPressed={handleExit}
    />
  );
}
